import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Laptop, Heart, Users, Armchair, LineChart, Gift,
  Check, ChevronRight, Sparkles, FileDown, Share2, Copy,
  ArrowLeft, ArrowRight, Loader2, Globe,
} from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Progress } from "../../ui/progress";
import { toast } from "sonner";
import { T, useT } from "../../../lib/mhmI18n";

/**
 * MyHomeMatrix v2 — Persona-Adaptive Flow.
 *
 * 13 screens. Every benefit card displays the persona-specific first-person
 * statement so the same benefit reads differently for a family vs investor vs senior.
 *
 * Bands (user-facing): Top Priorities (4) · Important (3) · Flexible (2) · Not Decision Drivers (3)
 * Engine keys (kept for back-compat with the existing scoring engine):
 *   top_priority=high, important=medium, flexible=low, not_decision_driver=eliminated.
 *
 * Spec source of truth: this component holds NO benefit copy. Persona statements
 * come from `/api/mhm/config` (`benefit.statements[persona_id]`).
 */

const PERSONA_ICONS = {
  working_professional_single: Laptop,
  couple_no_kids: Heart,
  family_with_kids: Users,
  retired_senior: Armchair,
  investor: LineChart,
  buying_for_family_others: Gift,
};

const STEPS = [
  "language", "opening", "persona", "confirm",
  "top", "important", "flexible", "ndd_review", "summary",
  "pairwise", "reflection", "budget", "capture", "results",
];

// ===== Tiny presentational helpers =====
const SectionShell = ({ children, narrow = false }) => (
  <div className={`mx-auto px-4 py-6 ${narrow ? "max-w-xl" : "max-w-3xl"}`}>{children}</div>
);

const StepHeader = ({ title, subtitle }) => (
  <div className="mb-5 text-center">
    <h2 className="text-xl md:text-2xl font-semibold font-['Manrope'] text-[#2C3E50]">{title}</h2>
    {subtitle && <p className="text-sm text-[#6D6660] mt-2 whitespace-pre-line">{subtitle}</p>}
  </div>
);

const ProgressBar = ({ step }) => {
  const idx = STEPS.indexOf(step);
  const total = STEPS.length - 1;
  const pct = idx <= 0 ? 0 : Math.round((idx / total) * 100);
  return (
    <div className="mb-4">
      <Progress value={pct} className="h-1 bg-[#EDE6DD]" data-testid="mhm-progress" />
    </div>
  );
};

// Persona-adaptive benefit card. Tone-toned per band.
const BenefitCard = ({ benefit, statement, label, selected, disabled, onClick, accent = "top", compact = false, testId }) => {
  const styles = {
    top: { active: "border-[#87A96B] bg-[#F0F5EC]", check: "bg-[#87A96B] text-white" },
    important: { active: "border-[#C4835A] bg-[#FBF3EE]", check: "bg-[#C4835A] text-white" },
    flexible: { active: "border-[#5B8A8A] bg-[#EAF0F0]", check: "bg-[#5B8A8A] text-white" },
    ndd: { active: "border-[#A6A09A] bg-[#F2EFEB]", check: "bg-[#A6A09A] text-white" },
  };
  const s = styles[accent] || styles.top;
  const displayLabel = label || benefit?.label;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative w-full text-left p-3.5 rounded-2xl border transition-all ${
        selected
          ? s.active
          : disabled
          ? "border-[#E8E3DE] bg-[#FAF8F5] opacity-40 cursor-not-allowed"
          : "border-[#E8E3DE] bg-white hover:border-[#C4B8AC] hover:shadow-sm"
      }`}
      data-testid={testId}
    >
      {selected && (
        <div className={`absolute top-2 right-2 w-5 h-5 rounded-full ${s.check} flex items-center justify-center`}>
          <Check className="w-3 h-3" />
        </div>
      )}
      <p className={`font-semibold text-[#2C3E50] ${compact ? "text-sm" : "text-base"} pr-6`}>{displayLabel}</p>
      {statement && (
        <p className="text-[13px] text-[#6D6660] mt-1.5 leading-snug">"{statement}"</p>
      )}
    </button>
  );
};

// ===================================================================

const MhMFlow = ({ apiBase, leadId, linkToken, isPublicView = false, showShareButton = false }) => {
  const [step, setStep] = useState("language");
  const [lang, setLang] = useState("en");
  const t = useT(lang);
  const [loading, setLoading] = useState(false);

  // Config from backend
  const [benefits, setBenefits] = useState([]);
  const [personas, setPersonas] = useState([]);

  // Session
  const [sessionId, setSessionId] = useState(null);
  const [persona, setPersona] = useState(null);            // full persona object
  const [topPriorities, setTopPriorities] = useState([]);
  const [important, setImportant] = useState([]);
  const [flexible, setFlexible] = useState([]);
  const [notDecisionDrivers, setNotDecisionDrivers] = useState([]);
  const [pairwisePairs, setPairwisePairs] = useState([]);
  const [pairwiseResults, setPairwiseResults] = useState([]);
  const [pairIdx, setPairIdx] = useState(0);
  const [scenarioResponse, setScenarioResponse] = useState(null);
  const [budget, setBudget] = useState({ emi_comfort: "" });
  const [leadData, setLeadData] = useState({ name: "", phone: "", email: "", city: "" });
  const [fingerprint, setFingerprint] = useState(null);
  const [buyerReport, setBuyerReport] = useState(null);

  // ===== Bootstrap config =====
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${apiBase}/mhm/config`);
        if (cancelled) return;
        setBenefits(r.data.factors || []);
        setPersonas(r.data.personas || []);
      } catch {
        toast.error(t.errLoadConfig);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  // ===== Helpers =====
  const benefitMap = Object.fromEntries(benefits.map((b) => [b.key, b]));
  const personaKey = persona?.key;
  // Persona statement in the chosen language
  const statementFor = (benefit) => {
    if (!benefit) return "";
    const dict = lang === "hi" ? (benefit.statements_hi || benefit.statements || {}) : (benefit.statements || {});
    const fallback = (benefit.statements || {})[personaKey] || "";
    return dict[personaKey] || fallback;
  };
  // Benefit/persona label in the chosen language
  const labelFor = (b) => (lang === "hi" && b?.label_hi) ? b.label_hi : (b?.label || b?.name || "");

  const remainingAfterTop = benefits.filter((b) => !topPriorities.includes(b.key));
  const remainingAfterImportant = remainingAfterTop.filter((b) => !important.includes(b.key));
  const remainingAfterFlexible = remainingAfterImportant.filter((b) => !flexible.includes(b.key));

  // ===== Step 0: Language picker =====
  const pickLanguage = (chosen) => {
    setLang(chosen);
    setStep("opening");
  };

  // ===== Step 1: Opening =====
  const beginSession = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${apiBase}/mhm/sessions`, { lead_id: leadId, link_token: linkToken, language: lang });
      setSessionId(r.data.session_id);
      setStep("persona");
    } catch {
      toast.error(t.errStart);
    } finally { setLoading(false); }
  };

  // ===== Step 2: Persona =====
  const choosePersona = async (p) => {
    setLoading(true);
    try {
      await axios.put(`${apiBase}/mhm/sessions/${sessionId}/persona`, { persona_id: p.key });
      setPersona(p);
      setStep("confirm");
    } catch {
      toast.error(t.errSavePersona);
    } finally { setLoading(false); }
  };

  // ===== Step 4: Top 4 =====
  const submitTop = async () => {
    if (topPriorities.length !== 4) {
      toast.error(t.topMustPick4);
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${apiBase}/mhm/sessions/${sessionId}/high`, { top_priorities: topPriorities });
      setStep("important");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.errSaveTop);
    } finally { setLoading(false); }
  };

  // ===== Step 5: Important 3 =====
  const submitImportant = async () => {
    if (important.length !== 3) {
      toast.error(t.importantMustPick3);
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${apiBase}/mhm/sessions/${sessionId}/medium`, { important });
      setStep("flexible");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.errSaveImportant);
    } finally { setLoading(false); }
  };

  // ===== Step 6: Flexible 2 → auto Not Decision Drivers 3 =====
  const submitFlexible = async () => {
    if (flexible.length !== 2) {
      toast.error(t.flexibleMustPick2);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.put(`${apiBase}/mhm/sessions/${sessionId}/finalize-bands`, { flexible });
      setNotDecisionDrivers(r.data.not_decision_drivers || []);
      setPairwisePairs(r.data.pairwise_pairs || []);
      setPairwiseResults([]);
      setPairIdx(0);
      setStep("ndd_review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.errSaveFlexible);
    } finally { setLoading(false); }
  };

  // ===== Step 9: Pairwise =====
  const recordPairwise = (winnerKey) => {
    const pair = pairwisePairs[pairIdx];
    const next = [...pairwiseResults, { factor_a: pair.factor_a, factor_b: pair.factor_b, winner: winnerKey }];
    setPairwiseResults(next);
    if (pairIdx + 1 < pairwisePairs.length) {
      setPairIdx(pairIdx + 1);
    } else {
      submitPairwise(next);
    }
  };

  const submitPairwise = async (results) => {
    setLoading(true);
    try {
      await axios.put(`${apiBase}/mhm/sessions/${sessionId}/pairwise`, { pairwise_results: results });
      setStep("reflection");
    } catch {
      toast.error(t.errSavePairwise);
    } finally { setLoading(false); }
  };

  // ===== Step 10: Reflection =====
  const submitReflection = async (response) => {
    setLoading(true);
    setScenarioResponse(response);
    try {
      await axios.put(`${apiBase}/mhm/sessions/${sessionId}/scenario`, { scenario_response: response });
      setStep("budget");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.errSaveReflection);
    } finally { setLoading(false); }
  };

  // ===== Step 11: Budget (optional) =====
  const submitBudget = async (skip = false) => {
    setLoading(true);
    try {
      if (!skip && budget.emi_comfort) {
        await axios.put(`${apiBase}/mhm/sessions/${sessionId}/budget`, budget).catch(() => {});
      }
      setStep(isPublicView && !leadId ? "capture" : "results");
      if (!isPublicView || leadId) calculateAndLoad();
    } catch {
      setStep(isPublicView && !leadId ? "capture" : "results");
    } finally { setLoading(false); }
  };

  // ===== Step 12: Capture (campaign mode) =====
  const submitCapture = async () => {
    if (!leadData.name || !leadData.phone) {
      toast.error(t.captureMissing);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${apiBase}/mhm/sessions/${sessionId}/capture-lead`, { ...leadData, link_token: linkToken });
      setStep("results");
      calculateAndLoad();
    } catch (e) {
      // Surface as much diagnostic info as we can — bare "Could not save"
      // is useless when the backend is actually 500ing.
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const msg = detail
        || (status ? `${t.errSaveCapture} (HTTP ${status})` : t.errSaveCapture)
        || e?.message
        || t.errSaveCapture;
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("MhM capture-lead failed", { status, detail, error: e });
    } finally { setLoading(false); }
  };

  // ===== Step 13: Calculate + load report =====
  const calculateAndLoad = async () => {
    setLoading(true);
    try {
      const calc = await axios.post(`${apiBase}/mhm/sessions/${sessionId}/calculate`);
      setFingerprint(calc.data);
      try {
        const rep = await axios.post(`${apiBase}/mhm/sessions/${sessionId}/generate-report`);
        setBuyerReport(rep.data);
      } catch { /* report is optional */ }
    } catch {
      toast.error(t.errCalculate);
    } finally { setLoading(false); }
  };

  // ===== Sharing =====
  const sharePublicReport = () => {
    if (!sessionId) return;
    const url = `${window.location.origin}/mhm/report/${sessionId}?lang=${lang}`;
    navigator.clipboard.writeText(url);
    toast.success(t.shareCopied);
  };

  // ============ RENDER PER STEP ============

  if (benefits.length === 0 && !["opening", "language"].includes(step)) {
    return <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#87A96B]" /></div>;
  }

  return (
    <div className="bg-[#FAF8F5] min-h-[60vh] rounded-xl">
      {!["opening", "language"].includes(step) && <ProgressBar step={step} />}

      {/* ---------- 0. LANGUAGE PICKER ---------- */}
      {step === "language" && (
        <SectionShell narrow>
          <div className="text-center pt-10 pb-2">
            <div className="w-14 h-14 rounded-2xl bg-[#87A96B] flex items-center justify-center mx-auto mb-5">
              <Globe className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2C3E50] font-['Manrope']">{t.appName}</h1>
            <p className="text-base text-[#6D6660] mt-3">{T.en.chooseLanguage} / {T.hi.chooseLanguage}</p>
            <p className="text-xs text-[#8B8580] mt-1">{T.en.languageHelp}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
              <button
                onClick={() => pickLanguage("en")}
                className="p-5 rounded-2xl border border-[#E8E3DE] bg-white hover:border-[#87A96B] hover:bg-[#F0F5EC] transition-all text-center"
                data-testid="mhm-lang-en"
              >
                <p className="text-lg font-semibold text-[#2C3E50]">English</p>
                <p className="text-xs text-[#6D6660] mt-1">Continue in English</p>
              </button>
              <button
                onClick={() => pickLanguage("hi")}
                className="p-5 rounded-2xl border border-[#E8E3DE] bg-white hover:border-[#87A96B] hover:bg-[#F0F5EC] transition-all text-center"
                data-testid="mhm-lang-hi"
              >
                <p className="text-lg font-semibold text-[#2C3E50]">Hinglish</p>
                <p className="text-xs text-[#6D6660] mt-1">Hindi + English mix mein continue karein</p>
              </button>
            </div>
          </div>
        </SectionShell>
      )}

      {/* ---------- 1. OPENING ---------- */}
      {step === "opening" && (
        <SectionShell narrow>
          <div className="text-center pt-8 pb-2">
            <div className="w-14 h-14 rounded-2xl bg-[#87A96B] flex items-center justify-center mx-auto mb-5">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2C3E50] font-['Manrope']">{t.appName}</h1>
            <p className="text-base text-[#6D6660] mt-3 leading-relaxed">
              {t.openingTagline}
            </p>
            <p className="text-sm text-[#8B8580] mt-2 leading-relaxed">
              {t.openingSub}
            </p>
            <Button
              size="lg"
              onClick={beginSession}
              disabled={loading}
              className="mt-8 bg-[#87A96B] hover:bg-[#76955d] text-white px-7 rounded-full"
              data-testid="mhm-start-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t.openingCta} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </SectionShell>
      )}

      {/* ---------- 2. PERSONA SELECT ---------- */}
      {step === "persona" && (
        <SectionShell>
          <StepHeader title={t.personaQuestion} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {personas.map((p) => {
              const Icon = PERSONA_ICONS[p.key] || Users;
              const personaLabel = (lang === "hi" && p.label_hi) ? p.label_hi : (p.label || p.name);
              return (
                <button
                  key={p.key}
                  onClick={() => choosePersona(p)}
                  disabled={loading}
                  className="text-left p-4 rounded-2xl border border-[#E8E3DE] bg-white hover:border-[#87A96B] hover:shadow-sm transition-all flex items-center gap-3"
                  data-testid={`mhm-persona-${p.key}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-[#F0F5EC] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#87A96B]" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#2C3E50]">{personaLabel}</p>
                    {p.description && <p className="text-xs text-[#6D6660] mt-0.5 line-clamp-2">{p.description}</p>}
                  </div>
                </button>
              );
            })}
          </div>
        </SectionShell>
      )}

      {/* ---------- 3. PERSONA CONFIRMATION ---------- */}
      {step === "confirm" && persona && (
        <SectionShell narrow>
          <div className="text-center pt-6">
            <div className="w-14 h-14 rounded-2xl bg-[#F0F5EC] flex items-center justify-center mx-auto mb-4">
              {(() => {
                const Icon = PERSONA_ICONS[persona.key] || Users;
                return <Icon className="w-7 h-7 text-[#87A96B]" />;
              })()}
            </div>
            <p className="text-base text-[#2C3E50] whitespace-pre-line leading-relaxed">
              {lang === "hi" && persona.confirmation_hi ? persona.confirmation_hi : persona.confirmation}
            </p>
            <Button
              size="lg"
              onClick={() => setStep("top")}
              className="mt-7 bg-[#87A96B] hover:bg-[#76955d] text-white px-7 rounded-full"
              data-testid="mhm-confirm-btn"
            >
              {t.confirmCta} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </SectionShell>
      )}

      {/* ---------- 4. TOP 4 ---------- */}
      {step === "top" && (
        <SectionShell>
          <StepHeader
            title={t.topTitle}
            subtitle={t.topSelected(topPriorities.length)}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {benefits.map((b) => (
              <BenefitCard
                key={b.key}
                benefit={b}
                label={labelFor(b)}
                statement={statementFor(b)}
                selected={topPriorities.includes(b.key)}
                disabled={!topPriorities.includes(b.key) && topPriorities.length >= 4}
                onClick={() =>
                  setTopPriorities((prev) =>
                    prev.includes(b.key) ? prev.filter((k) => k !== b.key) : prev.length < 4 ? [...prev, b.key] : prev,
                  )
                }
                accent="top"
                testId={`mhm-top-${b.key}`}
              />
            ))}
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              size="lg"
              disabled={topPriorities.length !== 4 || loading}
              onClick={submitTop}
              className="bg-[#87A96B] hover:bg-[#76955d] text-white rounded-full px-6"
              data-testid="mhm-top-continue"
            >
              {t.topCta} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </SectionShell>
      )}

      {/* ---------- 5. IMPORTANT 3 ---------- */}
      {step === "important" && (
        <SectionShell>
          <StepHeader
            title={t.importantTitle}
            subtitle={t.importantHelp(important.length)}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {remainingAfterTop.map((b) => (
              <BenefitCard
                key={b.key}
                benefit={b}
                label={labelFor(b)}
                statement={statementFor(b)}
                selected={important.includes(b.key)}
                disabled={!important.includes(b.key) && important.length >= 3}
                onClick={() =>
                  setImportant((prev) =>
                    prev.includes(b.key) ? prev.filter((k) => k !== b.key) : prev.length < 3 ? [...prev, b.key] : prev,
                  )
                }
                accent="important"
                testId={`mhm-important-${b.key}`}
              />
            ))}
          </div>
          <div className="mt-6 flex justify-between">
            <Button variant="ghost" onClick={() => setStep("top")} className="text-[#6D6660]"><ArrowLeft className="w-4 h-4 mr-1" /> {t.back}</Button>
            <Button
              size="lg"
              disabled={important.length !== 3 || loading}
              onClick={submitImportant}
              className="bg-[#C4835A] hover:bg-[#a86f49] text-white rounded-full px-6"
              data-testid="mhm-important-continue"
            >
              {t.continue} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </SectionShell>
      )}

      {/* ---------- 6. FLEXIBLE 2 ---------- */}
      {step === "flexible" && (
        <SectionShell>
          <StepHeader
            title={t.flexibleTitle}
            subtitle={t.flexibleHelp(flexible.length)}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {remainingAfterImportant.map((b) => (
              <BenefitCard
                key={b.key}
                benefit={b}
                label={labelFor(b)}
                statement={statementFor(b)}
                selected={flexible.includes(b.key)}
                disabled={!flexible.includes(b.key) && flexible.length >= 2}
                onClick={() =>
                  setFlexible((prev) =>
                    prev.includes(b.key) ? prev.filter((k) => k !== b.key) : prev.length < 2 ? [...prev, b.key] : prev,
                  )
                }
                accent="flexible"
                testId={`mhm-flexible-${b.key}`}
              />
            ))}
          </div>
          <div className="mt-6 flex justify-between">
            <Button variant="ghost" onClick={() => setStep("important")} className="text-[#6D6660]"><ArrowLeft className="w-4 h-4 mr-1" /> {t.back}</Button>
            <Button
              size="lg"
              disabled={flexible.length !== 2 || loading}
              onClick={submitFlexible}
              className="bg-[#5B8A8A] hover:bg-[#4d7575] text-white rounded-full px-6"
              data-testid="mhm-flexible-continue"
            >
              {t.continue} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </SectionShell>
      )}

      {/* ---------- 7. NOT DECISION DRIVERS REVIEW ---------- */}
      {step === "ndd_review" && (
        <SectionShell narrow>
          <StepHeader
            title={t.nddTitle}
            subtitle={t.nddSub}
          />
          <p className="text-xs uppercase tracking-wide text-[#8B8580] font-semibold mb-2 text-center">{t.nddSectionLabel}</p>
          <div className="space-y-2">
            {notDecisionDrivers.map((k) => {
              const b = benefitMap[k];
              if (!b) return null;
              return (
                <div key={k} className="p-3.5 rounded-2xl border border-[#E8E3DE] bg-[#F2EFEB]" data-testid={`mhm-ndd-${k}`}>
                  <p className="font-semibold text-[#2C3E50]">{labelFor(b)}</p>
                  <p className="text-[13px] text-[#6D6660] mt-1">"{statementFor(b)}"</p>
                </div>
              );
            })}
          </div>
          <div className="mt-6 flex justify-end">
            <Button size="lg" onClick={() => setStep("summary")} className="bg-[#87A96B] hover:bg-[#76955d] text-white rounded-full px-6" data-testid="mhm-ndd-continue">
              {t.nddCta} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </SectionShell>
      )}

      {/* ---------- 8. SUMMARY ---------- */}
      {step === "summary" && (
        <SectionShell>
          <StepHeader
            title={t.summaryTitle}
            subtitle={t.summarySub}
          />
          {[
            { title: t.summaryTop, keys: topPriorities, accent: "top" },
            { title: t.summaryImportant, keys: important, accent: "important" },
            { title: t.summaryFlexible, keys: flexible, accent: "flexible" },
            { title: t.summaryNdd, keys: notDecisionDrivers, accent: "ndd" },
          ].map((band) => (
            <div key={band.title} className="mb-5" data-testid={`mhm-summary-${band.accent}`}>
              <p className="text-xs uppercase tracking-wide text-[#8B8580] font-semibold mb-2">{band.title}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {band.keys.map((k) => {
                  const b = benefitMap[k];
                  return b ? (
                    <BenefitCard key={k} benefit={b} label={labelFor(b)} statement={statementFor(b)} selected accent={band.accent} compact />
                  ) : null;
                })}
              </div>
            </div>
          ))}
          <div className="mt-3 flex justify-end">
            <Button size="lg" onClick={() => setStep("pairwise")} className="bg-[#87A96B] hover:bg-[#76955d] text-white rounded-full px-6" data-testid="mhm-summary-continue">
              {t.continue} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </SectionShell>
      )}

      {/* ---------- 9. PAIRWISE ---------- */}
      {step === "pairwise" && pairwisePairs[pairIdx] && (() => {
        const pair = pairwisePairs[pairIdx];
        const a = benefitMap[pair.factor_a];
        const b = benefitMap[pair.factor_b];
        return (
          <SectionShell narrow>
            <StepHeader
              title={t.pairwiseTitle}
              subtitle={t.pairwiseStep(pairIdx + 1, pairwisePairs.length)}
            />
            <div className="space-y-3">
              {[a, b].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => recordPairwise(opt.key)}
                  className="w-full text-left p-4 rounded-2xl border border-[#E8E3DE] bg-white hover:border-[#87A96B] hover:bg-[#F0F5EC] transition-all"
                  data-testid={`mhm-pair-${opt.key}`}
                >
                  <p className="font-semibold text-[#2C3E50]">{labelFor(opt)}</p>
                  <p className="text-[13px] text-[#6D6660] mt-1.5">"{statementFor(opt)}"</p>
                </button>
              ))}
            </div>
          </SectionShell>
        );
      })()}

      {/* ---------- 10. REFLECTION ---------- */}
      {step === "reflection" && persona && (
        <SectionShell narrow>
          <StepHeader title={t.reflectionTitle} />
          <p className="text-[15px] text-[#2C3E50] whitespace-pre-line leading-relaxed bg-white border border-[#E8E3DE] rounded-2xl p-4">
            {(lang === "hi" && (persona.reflection_hi?.text || persona.scenario_hi?.text))
              || persona.reflection?.text
              || persona.scenario?.text}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-5">
            {[
              { key: "strongly_agree", label: t.reflectionStrongly },
              { key: "somewhat_agree", label: t.reflectionSomewhat },
              { key: "not_really", label: t.reflectionNotReally },
            ].map((opt) => (
              <Button
                key={opt.key}
                variant="outline"
                onClick={() => submitReflection(opt.key)}
                disabled={loading}
                className="rounded-full border-[#87A96B] text-[#2C3E50] hover:bg-[#F0F5EC]"
                data-testid={`mhm-reflection-${opt.key}`}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </SectionShell>
      )}

      {/* ---------- 11. BUDGET (optional) ---------- */}
      {step === "budget" && (
        <SectionShell narrow>
          <StepHeader title={t.budgetTitle} subtitle={t.budgetSub} />
          <div className="space-y-2">
            {[
              { key: "under_30k", label: t.budgetBuckets.under_30k },
              { key: "30k_50k", label: t.budgetBuckets["30k_50k"] },
              { key: "50k_75k", label: t.budgetBuckets["50k_75k"] },
              { key: "75k_1l", label: t.budgetBuckets["75k_1l"] },
              { key: "1l_1_5l", label: t.budgetBuckets["1l_1_5l"] },
              { key: "above_1_5l", label: t.budgetBuckets.above_1_5l },
            ].map((opt) => {
              const sel = budget.emi_comfort === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setBudget({ emi_comfort: opt.key })}
                  className={`w-full text-left p-3.5 rounded-2xl border transition-all ${
                    sel
                      ? "border-[#87A96B] bg-[#F0F5EC]"
                      : "border-[#E8E3DE] bg-white hover:border-[#C4B8AC]"
                  }`}
                  data-testid={`mhm-budget-${opt.key}`}
                >
                  <p className="text-sm font-medium text-[#2C3E50]">{opt.label}</p>
                </button>
              );
            })}
          </div>
          <div className="mt-5 flex justify-between items-center">
            <Button variant="ghost" onClick={() => submitBudget(true)} className="text-[#6D6660]" data-testid="mhm-budget-skip">{t.skip}</Button>
            <Button size="lg" onClick={() => submitBudget(false)} disabled={loading} className="bg-[#87A96B] hover:bg-[#76955d] text-white rounded-full px-6" data-testid="mhm-budget-continue">
              {t.continue} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </SectionShell>
      )}

      {/* ---------- 12. LEAD CAPTURE (campaign mode only) ---------- */}
      {step === "capture" && (
        <SectionShell narrow>
          <StepHeader title={t.captureTitle} />
          <div className="space-y-3">
            <Input placeholder={t.captureName} value={leadData.name}
              onChange={(e) => setLeadData({ ...leadData, name: e.target.value })} data-testid="mhm-capture-name" />
            <Input placeholder={t.capturePhone} value={leadData.phone}
              onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })} data-testid="mhm-capture-phone" />
            <Input placeholder={t.captureEmail} value={leadData.email}
              onChange={(e) => setLeadData({ ...leadData, email: e.target.value })} data-testid="mhm-capture-email" />
            <Input placeholder={t.captureCity} value={leadData.city}
              onChange={(e) => setLeadData({ ...leadData, city: e.target.value })} data-testid="mhm-capture-city" />
          </div>
          <Button size="lg" onClick={submitCapture} disabled={loading} className="mt-5 w-full bg-[#87A96B] hover:bg-[#76955d] text-white rounded-full" data-testid="mhm-capture-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {t.captureCta}
          </Button>
        </SectionShell>
      )}

      {/* ---------- 13. RESULTS ---------- */}
      {step === "results" && (
        <SectionShell>
          <StepHeader title={t.resultsTitle} />
          {!fingerprint && (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#87A96B]" />
              <p className="text-sm text-[#6D6660] mt-3">{t.resultsBuilding}</p>
            </div>
          )}
          {fingerprint && (
            <div className="space-y-4" data-testid="mhm-results">
              {/* Top 3 callout */}
              <div className="bg-white border border-[#E8E3DE] rounded-2xl p-4">
                <p className="text-xs uppercase tracking-wide text-[#8B8580] font-semibold mb-2">{t.resultsTop3}</p>
                <div className="space-y-2">
                  {(fingerprint.ranked_factors || []).slice(0, 3).map((r, i) => {
                    const b = benefitMap[r.factor_id];
                    if (!b) return null;
                    return (
                      <div key={r.factor_id} className="flex items-baseline gap-3">
                        <span className="text-2xl font-bold text-[#87A96B] font-['Manrope']">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#2C3E50]">{labelFor(b)}</p>
                          <p className="text-[12px] text-[#6D6660]">"{statementFor(b)}"</p>
                        </div>
                        <span className="text-sm font-semibold text-[#87A96B]">{r.weight}/10</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Weight bars */}
              <div className="bg-white border border-[#E8E3DE] rounded-2xl p-4">
                <p className="text-xs uppercase tracking-wide text-[#8B8580] font-semibold mb-3">{t.resultsFull}</p>
                <div className="space-y-1.5">
                  {(fingerprint.ranked_factors || []).map((r) => {
                    const b = benefitMap[r.factor_id];
                    if (!b) return null;
                    const w = r.weight;
                    const color = w >= 7 ? "#87A96B" : w >= 4 ? "#C4835A" : w > 0 ? "#5B8A8A" : "#C4B8AC";
                    return (
                      <div key={r.factor_id} className="flex items-center gap-3">
                        <p className="text-[13px] text-[#2C3E50] w-1/2 truncate">{labelFor(b)}</p>
                        <div className="flex-1 h-2 bg-[#F2EFEB] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${w * 10}%`, backgroundColor: color }} />
                        </div>
                        <span className="text-[12px] text-[#6D6660] w-8 text-right">{w}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* AI Report */}
              {buyerReport ? (
                <div className="bg-white border border-[#E8E3DE] rounded-2xl p-4 space-y-3" data-testid="mhm-buyer-report">
                  {buyerReport.persona_summary && <p className="text-[14px] text-[#2C3E50] leading-relaxed">{buyerReport.persona_summary}</p>}
                  {buyerReport.decision_guidance && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-[#8B8580] font-semibold mb-1">{t.resultsHowToUse}</p>
                      <p className="text-[13px] text-[#6D6660] leading-relaxed">{buyerReport.decision_guidance}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-[#F0F5EC] border border-[#D9E5CF] rounded-2xl p-4 text-center">
                  <Loader2 className="w-4 h-4 animate-spin mx-auto text-[#87A96B] mb-1" />
                  <p className="text-xs text-[#6D6660]">{t.resultsReportLoading}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 justify-end pt-2">
                {sessionId && (
                  <Button variant="default" size="sm"
                    onClick={() => window.open(`${window.location.origin}/mhm/projects/${sessionId}`, "_blank")}
                    className="bg-[#87A96B] hover:bg-[#76955d] text-white"
                    data-testid="mhm-compare-homes-btn"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Compare My Homes
                  </Button>
                )}
                {showShareButton && sessionId && (
                  <Button variant="outline" size="sm" onClick={sharePublicReport} className="border-[#87A96B] text-[#2C3E50]" data-testid="mhm-share-btn">
                    <Share2 className="w-3.5 h-3.5 mr-1.5" /> {t.resultsShare}
                  </Button>
                )}
                {sessionId && (
                  <Button variant="outline" size="sm"
                    onClick={() => window.open(`${window.location.origin}/mhm/report/${sessionId}`, "_blank")}
                    className="border-[#C4835A] text-[#2C3E50]"
                    data-testid="mhm-open-report"
                  >
                    <FileDown className="w-3.5 h-3.5 mr-1.5" /> {t.resultsOpen}
                  </Button>
                )}
              </div>
            </div>
          )}
        </SectionShell>
      )}
    </div>
  );
};

export default MhMFlow;
